import React, { useState } from "react";
import config from "../../config/config";
import { ThemeProvider } from "onnit-ui/lib/themes/styled";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import usePageData from "../../hooks/usePageData";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import theme from "onnit-ui/lib/themes/light";
import { Img, Picture } from "onnit-ui/lib/image";
import Grid from "onnit-ui/lib/module/grid/12/Grid";
import Box from "onnit-ui/lib/box/Box";
import Text, { H2, List, ListItem, MultiColumnText, Sup } from "onnit-ui/lib/text";
import SupplementFacts from "../../components/common/SupplementFacts";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";

import MediaHero01 from "onnit-ui/lib/module/MediaHero01";
import Embed from "onnit-ui/lib/video/Embed";
import MediaCollage01 from "onnit-ui/lib/module/MediaCollage01";
import CopyDisplay02 from "onnit-ui/lib/module/CopyDisplay02";
import HeadlineLabelContent from "onnit-ui/lib/module/HeadlineLabelContent";
import GreensBlendSlider from "./GreensBlendSlider";
import ImageContentPortrait from "onnit-ui/lib/module/ImageContentPortrait";
import { Recipe } from "onnit-ui/lib/module/Recipe";
import MushroomBlendSlider from "./MushroomBlendSlider";

function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) return null;

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/shroom-tech-greens`;
    const IMG_PARAMS = config.IMG_PARAMS;
    const shroomTechGreen = "#495B48";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/BuyBox-1_mobile.jpg${IMG_PARAMS}`,
                                `${IMG_URL}/BuyBox-1.jpg${IMG_PARAMS}`,
                            ]}
                            alt="Shroom Tech Greens Box surrounded by vegetables."
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-2.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Opened box of Shroom Tech Greens."
                            width={460}
                            height={460}
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-3.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Mushrooms and Broccoli."
                            width={920}
                            height={920}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/BuyBox-4_mobile.jpg${IMG_PARAMS}`,
                                `${IMG_URL}/BuyBox-4.jpg${IMG_PARAMS}`,
                            ]}
                            alt="Hand pouring Shroom Tech Greens Packet into drink."
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box backgroundImage="linear-gradient(#495B48, #70C6AE)" py={[6, 6, 8, 10]} mt={[0, 0, 8, 10]}>
                        <MediaHero01
                            mb={[6, 6, 8, 10]}
                            media={
                                <Img
                                    src={`${IMG_URL}/MediaHero-1-mobile.jpg${IMG_PARAMS}`}
                                    alt="Broccoli Florets."
                                    width={552}
                                    height={552}
                                />
                            }
                            headline={
                                <H2 mb={0}>
                                    <Text typeStyle="headlineHero01" color="white">
                                        Shroom
                                    </Text>
                                    <Text typeStyle="headlineHero01" color="white">
                                        Tech
                                        <Sup variant="reg" />
                                    </Text>
                                    <Text typeStyle="headlineHero01" color="white">
                                        GREENS
                                    </Text>
                                </H2>
                            }
                        />

                        <Grid px={[4, 4, 5]} mb={4}>
                            <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 9"]}>
                                <Text typeStyle="copyHero01" color="white" as="p">
                                    Each serving of Shroom Tech
                                    <Sup variant="reg" /> GREENS contains the nutrient equivalent of 14 Kale leaves, 4
                                    Spinach Leaves, &amp; 1 Broccoli floret making it the ultimate ingredient for a
                                    superfood smoothie.
                                </Text>
                                <Text typeStyle="copyHero01" color="white" as="p" my={[4, 4, 4, 6]}>
                                    It’s Like a Garden in Your Pocket.
                                </Text>
                            </Box>
                        </Grid>

                        <Grid px={[4, 4, 5]}>
                            <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "3 / span 8"]}>
                                <Img
                                    my={[4, 4, 6]}
                                    src={`${IMG_URL}/Landscape-1.jpg${IMG_PARAMS}`}
                                    lazyLoad={false}
                                    alt="Single Shroom Tech Greens packet surrounded by broccoli."
                                    width={1888}
                                    height={1062}
                                />
                                <Text
                                    typeStyle="copyDisplay02"
                                    color="white"
                                    as="h2"
                                    textTransform="uppercase"
                                    mt={[6, 8]}
                                >
                                    Great For:
                                </Text>
                                <List mb={[6, 8]}>
                                    <ListItem
                                        color="white"
                                        ml={6}
                                        style={{ lineHeight: 2 }}
                                        pt={[2, 4]}
                                        typeStyle="copyHero02"
                                    >
                                        Low-carb and keto dieters
                                    </ListItem>
                                    <ListItem color="white" ml={6} style={{ lineHeight: 2 }} typeStyle="copyHero02">
                                        Frequent travelers
                                    </ListItem>
                                    <ListItem color="white" ml={6} style={{ lineHeight: 2 }} typeStyle="copyHero02">
                                        People who don’t always eat their vegetables!
                                    </ListItem>
                                </List>
                            </Box>
                        </Grid>

                        <HeadlineLabelContent
                            pb={[6, 6, 6, 8]}
                            label={
                                <Text typeStyle="copy05" color="white">
                                    Daily Vegetables
                                </Text>
                            }
                            headline={
                                <Text
                                    as="h2"
                                    typeStyle="headlineHero02"
                                    fontStyle="normal"
                                    style={{ textTransform: "none" }}
                                    color="white"
                                    lineHeight={3}
                                >
                                    Why Take Shroom
                                    <br />
                                    Tech
                                    <Sup variant="reg" /> GREENS?
                                </Text>
                            }
                            content={
                                <>
                                    <Text typeStyle="copyHero02" as="p" color="white">
                                        The USDA’s Dietary Guidelines call for adults to get 2 ½ cups of vegetables
                                        daily, and suggest more than that if you’re an athlete or live an active
                                        lifestyle. Still, research suggests that nearly 90% of Americans don’t get
                                        enough.
                                        <br />
                                        <br />
                                        Shroom Tech
                                        <Sup variant="reg" /> GREENS helps fill in some of the gaps in your diet,
                                        providing a serving (equivalent to 1/2 cup) of certified organic vegetables per
                                        packet.
                                    </Text>
                                </>
                            }
                        />

                        <Grid>
                            <Box gridColumn={["1 / -1", "1/-1", "1/-1", "3 / span 8"]} px={4}>
                                <Embed
                                    title="Shroom Tech GREENS product video"
                                    src="https://player.vimeo.com/video/526218046"
                                />
                            </Box>
                        </Grid>
                    </Box>

                    <Box display="grid" gridTemplateColumns="repeat(12, 1fr)">
                        <Box gridColumn={["1 / -1", "2 / -1"]} gridRow={1} px={4} pt={[6, 6, 8, 10]}>
                            <Text as="h2" typeStyle="headlineHero01" color={shroomTechGreen} mb={[5, 5, 8]}>
                                How Shroom
                                <br />
                                Tech
                                <Sup variant="reg" /> GREENS
                                <br />
                                Works
                            </Text>
                        </Box>

                        <Box
                            bg={["transparent", "rgba(218,217,219,0.2)"]}
                            gridColumn={["1 / -1", "4 / span 8"]}
                            gridRow={2}
                            p={[0, 5, 6]}
                            px={4}
                        >
                            <Text as="p" typeStyle="copyHero02" fontWeight="regular">
                                Shroom Tech
                                <Sup variant="reg" /> GREENS contains two special ingredient blends—the Onnit Organic
                                Greens Blend and the PeakO
                                <Text as="sub" display="inline" fontSize={1}>
                                    2
                                </Text>
                                <Sup variant="reg" /> Organic Mushroom Blend. The combination supports not only general
                                health but exercise performance as well.
                                <Sup variant="dagger" />
                            </Text>
                        </Box>
                    </Box>

                    <MediaCollage01
                        py={[6, 6, 8, 10]}
                        item1={
                            <Img
                                src={`${IMG_URL}/MediaCollage-1-1.jpg${IMG_PARAMS}`}
                                alt="Close up visual of broccoli."
                                width={448}
                                height={692}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/MediaCollage-1-2.jpg${IMG_PARAMS}`}
                                alt="Woman sitting on rock drinking water."
                                width={448}
                                height={692}
                            />
                        }
                        item3={
                            <Img
                                src={`${IMG_URL}/MediaCollage-1-3.jpg${IMG_PARAMS}`}
                                alt="Single packet of Shroom Tech Greens surrounded by broccoli."
                                width={928}
                                height={1280}
                            />
                        }
                    />

                    <Box bg="black" py={[6, 6, 6, 8]}>
                        <Box pb={6}>
                            <Grid>
                                <Box px={4} pt={6} pb={4} gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / 12"]}>
                                    <Text typeStyle="headlineHero02" color="white" fontSize={[6, 6, 6, 8, 8, 10, 10]}>
                                        Onnit Organic Greens Blend
                                    </Text>
                                </Box>
                            </Grid>
                            <GreensBlendSlider />
                            <Grid>
                                <Box px={4} py={6} gridColumn={["1 / -1", "1 / -1", "1 / -1", "3 / 12"]}>
                                    <Text as="p" color="white" typeStyle="copyHero02">
                                        Think of this as a collection of the vegetable ingredients you probably
                                        wouldn&rsquo;t eat as a kid (and probably still don&rsquo;t) ground into a fine
                                        powder. Offering trace amounts of many phytonutrients, an umbrella term that
                                        refers to various plant compounds, these greens promote overall vitality.
                                        <Sup variant="dagger" />
                                    </Text>
                                </Box>
                            </Grid>
                        </Box>

                        <Box py={6}>
                            <Grid>
                                <Box px={4} pt={6} pb={4} gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / 12"]}>
                                    <Text typeStyle="headlineHero02" color="white" fontSize={[6, 6, 6, 8, 8, 10, 10]}>
                                        PeakO<sub>2</sub>
                                        <Sup variant="reg" /> Organic Mushroom Blend
                                    </Text>
                                </Box>
                            </Grid>
                            <MushroomBlendSlider />
                            <Grid>
                                <Box px={4} py={6} gridColumn={["1 / -1", "1 / -1", "1 / -1", "3 / 12"]}>
                                    <Text as="p" color="white" typeStyle="copyHero02">
                                        Mushrooms have traditionally been used for a variety of functions (hence the
                                        creation of GREENS&rsquo; sister products, Shroom Tech
                                        <Sup variant="reg" /> SPORT and Shroom Tech
                                        <Sup variant="reg" /> IMMUNE). This blend of friendly fungi supports general
                                        health as well as exercise performance.
                                        <Sup variant="dagger" />
                                    </Text>
                                </Box>
                            </Grid>
                        </Box>
                    </Box>

                    <Grid px={[4, 4, 5]} my={[6, 6, 8, 10]}>
                        <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "3 / span 8"]}>
                            <Img
                                my={[4, 4, 4, 6]}
                                src={`${IMG_URL}/Landscape-2.jpg${IMG_PARAMS}`}
                                alt="Woman cutting fresh strawberries."
                                width={944}
                                height={531}
                            />
                        </Box>
                    </Grid>

                    <CopyDisplay02>
                        <Text typeStyle="copyDisplay02" mb={[6, 6, 6, 8]} style={{ textTransform: "none" }}>
                            The Importance of Organic
                        </Text>
                        <MultiColumnText>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={4}>
                                These days, pollution, pesticides, and depleted soil can result in crops that are often
                                less nutritious.
                                <br />
                                <br />
                                USDA certified organic vegetables are grown without the use of synthetic fertilizers,
                                artificial food additives, and genetically modified organisms (GMOs), and tend to
                                contain higher levels of nutrients than many conventionally-grown crops. In addition,
                                organic farming helps replenish soil and conserve water, which may make it easier on the
                                environment.
                                <br />
                                <br />
                                For these reasons, we made an extra effort to ensure that the veggies in Shroom Tech
                                <Sup variant="reg" /> GREENS are 100% organic.
                            </Text>
                        </MultiColumnText>
                    </CopyDisplay02>

                    <HeadlineLabelContent
                        pt={[6, 6, 8, 10]}
                        label={<Text typeStyle="copy05">Lifestyle</Text>}
                        headline={
                            <Text
                                as="h2"
                                typeStyle="headlineHero02"
                                fontStyle="normal"
                                style={{ textTransform: "none" }}
                                lineHeight={3}
                                color={shroomTechGreen}
                            >
                                Spruce Up Your Smoothie
                            </Text>
                        }
                        content={
                            <Text typeStyle="copyHero02" as="p" color="grays.4">
                                Shroom Tech
                                <Sup variant="reg" /> GREENS is unflavored and free of synthetic sweeteners, so it can
                                be added to your usual protein shake or smoothie with minimal impact on flavor.
                                <br />
                                <br />
                                We think Shroom Tech
                                <Sup variant="reg" /> GREENS makes a fine addition to the following smoothies.
                            </Text>
                        }
                    />
                    <ImageContentPortrait
                        my={[6, 6, 8, 10]}
                        image={
                            <Img
                                src={`${IMG_URL}/ContentPortrait-1.jpg${IMG_PARAMS}`}
                                alt="Smoothie topped with coffee beans."
                                width={928}
                                height={1200}
                            />
                        }
                        content={
                            <Recipe
                                textColor={shroomTechGreen}
                                recipe={{
                                    name: "Choconut Joy Smoothie",
                                    image: `${IMG_URL}/ContentPortrait-1.jpg${IMG_PARAMS}`,
                                    datePublished: "2021-03-01",
                                    prepTime: "PT5M",
                                    recipeYield:
                                        '24 <span aria-hidden="true">oz</span><span class="sr-only">ounces</span> Smoothie',
                                    recipeCategory: "Smoothie",
                                    recipeIngredient: [
                                        '1 packet Shroom Tech<span class="oui-sup">&reg;</span> GREENS',
                                        "2 scoops whey protein, chocolate flavor",
                                        '2 <span aria-hidden="true">tsp</span><span class="sr-only">teaspoons of</span> nut butter<br />(such as almond, peanut, cashew; your choice)',
                                        "Dash of salt",
                                        '1 <span aria-hidden="true">tsp</span><span class="sr-only">teaspoon of</span> raw honey',
                                        "1 banana, frozen",
                                        "1 cup unsweetened coconut milk",
                                        "2 cups ice",
                                    ],
                                    recipeInstructions: "Add all ingredients to a blender and pulse until smooth.",
                                }}
                            />
                        }
                    />

                    <ImageContentPortrait
                        my={[6, 6, 8, 10]}
                        image={
                            <Img
                                src={`${IMG_URL}/ContentPortrait-2.jpg${IMG_PARAMS}`}
                                alt="Green Smoothie topped with cacao nibs."
                                width={464}
                                height={600}
                            />
                        }
                        content={
                            <Recipe
                                textColor={shroomTechGreen}
                                recipe={{
                                    name: "Keto Mint Chip Smoothie",
                                    image: `${IMG_URL}/ContentPortrait-2.jpg${IMG_PARAMS}`,
                                    datePublished: "2021-03-01",
                                    prepTime: "PT5M",
                                    recipeYield:
                                        '24 <span aria-hidden="true">oz</span><span class="sr-only">ounces</span> Smoothie',
                                    recipeCategory: "Smoothie",
                                    recipeIngredient: [
                                        '1 packet Shroom Tech<span class="oui-sup">&reg;</span> GREENS',
                                        "2 scoops whey protein, vanilla flavor",
                                        '2 <span aria-hidden="true">tsp</span><span class="sr-only">teaspoon of</span> chia seeds',
                                        '½ <span aria-hidden="true">tbsp</span><span class="sr-only">tablespoon of</span> ground cinnamon',
                                        '2 <span aria-hidden="true">tbsp</span><span class="sr-only">tablespoon of</span> almond butter',
                                        '½ <span aria-hidden="true">tsp</span><span class="sr-only">teaspoon of</span> peppermint flavor',
                                        "Dash of salt",
                                        "2 cups of ice",
                                        "1 cup unsweetened vanilla almond milk",
                                        '1 <span aria-hidden="true">tbsp</span><span class="sr-only">tablespoon of</span> cacao nibs',
                                    ],
                                    recipeInstructions: [
                                        "Add all ingredients to a blender, except for the cacao nibs and pulse until smooth",
                                        "Add the cacao nibs and blend or another two seconds. Pour and enjoy!",
                                    ],
                                }}
                            />
                        }
                    />

                    <ImageContentPortrait
                        my={[6, 6, 8, 10]}
                        image={
                            <Img
                                src={`${IMG_URL}/ContentPortrait-3.jpg${IMG_PARAMS}`}
                                alt="Smoothie next to fresh fruit."
                                width={464}
                                height={600}
                            />
                        }
                        content={
                            <Recipe
                                textColor={shroomTechGreen}
                                recipe={{
                                    name: "Brain &amp; Brawn Smoothie",
                                    image: `${IMG_URL}/ContentPortrait-3.jpg${IMG_PARAMS}`,
                                    datePublished: "2021-03-01",
                                    prepTime: "PT5M",
                                    recipeYield:
                                        '24 <span aria-hidden="true">oz</span><span class="sr-only">ounces</span> Smoothie.',
                                    recipeCategory: "Smoothie",
                                    recipeIngredient: [
                                        '1 packet Shroom Tech<span class="oui-sup">&reg;</span> GREENS',
                                        '1 packet Alpha BRAIN<span class="oui-sup">&reg;</span> Instant Peach',
                                        "1 scoop whey protein, vanilla or chocolate flavor",
                                        "¼ cup oat bran",
                                        "1 medium banana",
                                        '1 <span aria-hidden="true">tbsp</span><span class="sr-only">tablespoon of</span> nut butter',
                                        '1 <span aria-hidden="true">tbsp</span><span class="sr-only">tablespoon of</span> hemp seeds',
                                        '1 <span aria-hidden="true">tbsp</span><span class="sr-only">tablespoon of</span>  chia seeds',
                                        '1 <span aria-hidden="true">tbsp</span><span class="sr-only">tablespoon of</span>  MCT oil',
                                        "2 cups unsweetened rice milk",
                                        "1 cup ice",
                                    ],
                                    recipeInstructions: "Place all ingredients in a blender and mix until frothy",
                                }}
                            />
                        }
                    />

                    <Grid px={[4, 4, 5]} my={[6, 6, 8, 10]}>
                        <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "3 / span 8"]}>
                            <Img
                                my={[4, 4, 6]}
                                src={`${IMG_URL}/Landscape-3.jpg${IMG_PARAMS}`}
                                alt="Box of Shroom Tech GREENS surrounded by broccoli."
                                width={944}
                                height={531}
                            />
                        </Box>
                    </Grid>
                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers
                        supplementFacts={<SupplementFacts currentPID={currentPID} />}
                        fdaWarning={false}
                    >
                        <Text typeStyle="caption02" mb={4}>
                            <b>WARNING:</b> Consult a medical doctor before taking this or any other nutritional
                            supplement if you are pregnant, nursing, have or suspect a medical condition, or are taking
                            any medications. Consume once daily or as needed. <b>KEEP OUT OF REACH OF CHILDREN.</b>
                        </Text>
                        <Text typeStyle="caption02" as="p" fontWeight={"bold"} mb={4}>
                            <Sup variant="dagger" /> THESE STATEMENTS HAVE NOT BEEN EVALUATED BY THE FOOD AND DRUG
                            ADMINISTRATION. THIS PRODUCT IS NOT INTENDED TO DIAGNOSE, TREAT, CURE, OR PREVENT ANY
                            DISEASE.
                        </Text>
                        <Text typeStyle="caption02" as="p" fontWeight={"bold"} mb={4}>
                            PeakO
                            <Text as="sub" display="inline" style={{ fontSize: "8px" }}>
                                2
                            </Text>
                            <Sup variant="reg" /> is protected by patents pending owned by Disruptive Nutrition, LLC and
                            under exclusive global distribution by Compound Solutions, Inc.
                        </Text>
                        <Text typeStyle="caption02" as="p" fontWeight={"bold"} mb={4}>
                            <Sup variant="dagger" />
                            <Sup variant="dagger" /> TruServ Verified
                            <Sup variant="reg" />
                        </Text>
                    </UsageAndDisclaimers>
                )}
            </>
        </ThemeProvider>
    );
}

export default Page;
